/**
 * Resolved width of a (sticky) column: the measured DOM width when available, else the configured
 * calcWidth, else 0. Extracted from {@link Table} to keep that file under the size cap.
 */
export const stickyColumnWidth = (
	col: { uid?: string; calcWidth?: number },
	measuredWidths: Record<string, number>
): number => {
	if (col.uid && measuredWidths[col.uid]) {
		return measuredWidths[col.uid];
	}

	return col.calcWidth || 0;
};

/**
 * Advance a column's tri-state sort on click (none → ASC → DESC → none), resetting any other
 * sorted column. Extracted from {@link Table}'s `handleSortColumn` to keep that file under the
 * size cap; returns the updated column array and the clicked column's new orderStatus.
 */
export const cycleColumnSort = <T extends { orderStatus?: number }>(
	columnData: T[],
	sortedColumn: number | null,
	index: number
): { newColumnData: T[]; orderStatus: number } => {
	const newColumnData = [...columnData];

	if (index === sortedColumn) {
		newColumnData[index].orderStatus = ((newColumnData[index].orderStatus ?? 0) + 1) % 3;
	} else {
		if (sortedColumn !== null && newColumnData[sortedColumn]) {
			newColumnData[sortedColumn].orderStatus = 0;
		}

		newColumnData[index].orderStatus = 1;
	}

	return { newColumnData, orderStatus: newColumnData[index].orderStatus ?? 0 };
};

/**
 * Flip a row's expanded state, returning a new Set (React state must not be
 * mutated). Lives here so `Table.tsx` — over the length ratchet — stays smaller.
 */
export const toggleExpandedRow = <T>(rows: Set<T>, id: T): Set<T> => {
	const next = new Set(rows);

	if (next.has(id)) {
		next.delete(id);
	} else {
		next.add(id);
	}

	return next;
};

/**
 * Ids of rows that should auto-expand now (defaultExpanded tables): every row not yet seeded.
 * Mutates `seeded` by recording them, so a row the user later collapses is never re-expanded
 * when the next page appends to the data array. Returns null when nothing new appeared.
 */
export const seedDefaultExpandedRows = (
	seeded: Set<number | string>,
	rows: Array<{ id: number | string }>
): Array<number | string> | null => {
	const fresh = rows.filter((row) => !seeded.has(row.id)).map((row) => row.id);

	if (fresh.length === 0) {
		return null;
	}

	fresh.forEach((id) => seeded.add(id));

	return fresh;
};
